import { valueOf } from "@/types/common";

export const USER_TYPE = {
  ADMIN: "admin",
  ADMIN_MEMBER: "admin_member",
  COMPANY_USER: "company_user",
};

export type TUserType = valueOf<typeof USER_TYPE>;

export const ADMIN_ROLE = {
  MANAGER: "manager",
  SALE: "company_sale",
  CUSTOM_SERVICE: "custom_service",
  AUDITOR: "auditor",
};

export type TAdminRole = valueOf<typeof ADMIN_ROLE>;

export const TALENT_STATUS = {
  OFFERED: "offered",
  WORKING: "working",
  LEFT: "left",
  RETIRED: "retired",
};

export type TTalentStatus = valueOf<typeof TALENT_STATUS>;

export const TALENT_STATUS_LABEL = {
  [TALENT_STATUS.OFFERED]: "内定",
  [TALENT_STATUS.WORKING]: "在籍",
  [TALENT_STATUS.LEFT]: "休職",
  [TALENT_STATUS.RETIRED]: "退職",
};

export const COMPANY_STATUS = {
  DURING_CONTRACT: "during_contract",
  CANCELLED_CONTRACT: "canceled_contract",
  TRIAL: "trial",
};

export type TCompanyStatus = valueOf<typeof COMPANY_STATUS>;

export const CONDITION_DOCUMENT_STATUS = {
  NOT_CREATED: "not_created",
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
};

export type TConditionDocumentStatus = valueOf<typeof CONDITION_DOCUMENT_STATUS>;

export const CONDITION_DOCUMENT_STATUS_LABEL = {
  [CONDITION_DOCUMENT_STATUS.NOT_CREATED]: "未作成",
  [CONDITION_DOCUMENT_STATUS.IN_PROGRESS]: "作成中",
  [CONDITION_DOCUMENT_STATUS.COMPLETE]: "完了",
};

export const RSO_DIVISION = {
  CORPORATE: "corporate",
  INDIVIDUAL: "individual",
};

export type TRsoDivision = valueOf<typeof RSO_DIVISION>;

export const COMPANY_DIVISION = {
  CORPORATE: "corporate",
  SOLE_PROPRIETOR: "sole_proprietor",
};

export type TCompanyDivision = valueOf<typeof COMPANY_DIVISION>;

export const COMPANY_SPECIFIC_INDUSTRY_CODE = {
  RESTAURANT: 1, // 外食
  FOOD_MANUFACTURING: 2, // 飲食料品製造
  FISHERY: 3, // 漁業
  AGRICULTURE: 4, // 農業
  ACCOMMODATION: 5, // 宿泊
  AVIATION: 6, // 航空
  AUTOMOBILE_MAINTENANCE: 7, // 自動車整備
  SHIPBUILDING: 8, // 造船・舶用工
  CONSTRUCTION: 9, // 建設
  INDUSTRIAL_PRODUCT_MANUFACTURING: 10, // 工業製品製造
  BUILDING_CLEANING: 11, // ビルクリーニング
  NURSING_CARE: 12, // 介護
  WOOD_INDUSTRY: -1, // 木材産業
  FORESTRY: -1, // 林業
  AUTOMOBILE_TRANSPORTATION: -1, // 自動車運送業
  RAILWAY: -1, // 鉄道
};

export type TCompanySpecificIndustryCode = valueOf<typeof COMPANY_SPECIFIC_INDUSTRY_CODE>;

export const GENDER = {
  MALE: 1,
  FEMALE: 2,
  OTHER: 3,
} as const;

export type TGender = valueOf<typeof GENDER>;

export const GENDER_LABEL = {
  [GENDER.MALE]: "男性",
  [GENDER.FEMALE]: "女性",
  [GENDER.OTHER]: "その他",
};

export const DOCUMENT_KEYS = {
  ACCEPTANCE_ACTIVITY: "acceptance_activity",
  LIFE_SUPPORT: "life_support",
  ANY_TIME: "any_time",
  RETIREMENT_ACCEPTING_WORKERS: "retirement_accepting_workers",
  RETIREMENT_TALENT_CONTRACT: "retirement_TALENT_contract",
};

export type TDocumentKey = valueOf<typeof DOCUMENT_KEYS>;

export const DOCUMENT_STEP_KEYS = {
  ACCEPTANCE_ACTIVITY_CREATE_DOCUMENT: "acceptance_activity_create_document",
  ACCEPTANCE_ACTIVITY_UPLOAD_DOCUMENT: "acceptance_activity_upload_document",
  ACCEPTANCE_ACTIVITY_SUBMIT: "acceptance_activity_submit",
  LIFE_SUPPORT_CREATE_DOCUMENT: "life_support_create_document",
  LIFE_SUPPORT_CONFIRM_DISCUSSION_RECORD: "life_support_confirm_discussion_record",
  LIFE_SUPPORT_SUBMIT: "life_support_activity_submit",
  ANY_TIME_CREATE_DOCUMENT: "any_time_create_document",
  ANY_TIME_UPLOAD_DOCUMENT: "any_time_upload_document",
  ANY_TIME_SUBMIT: "any_time_submit",
  RENEW_VISA_BASIC_INFORMATION: "renew_visa_basic_information",
  RENEW_VISA_CREATE_DOCUMENT: "renew_visa_create_document",
  RENEW_VISA_SUBMIT: "renew_visa_submit",
  RENEW_VISA_AFTER_SUBMIT_APPLICATION: "renew_visa_after_submit_application",
  RENEW_VISA_AFTER_SUBMIT_APPLICATION_RESULT: "renew_visa_after_submit_application_result",
  RENEW_VISA_UPDATE_RESIDENCE_CARD: "renew_visa_update_residence_card",
  RETIREMENT_ACCEPTING_WORKERS_CREATE_DOCUMENT: "retirement_accepting_workers_create_document",
  RETIREMENT_ACCEPTING_WORKERS_CREATE_CONVERT_REQUEST: "retirement_accepting_workers_create_convert_request",
  RETIREMENT_ACCEPTING_WORKERS_SUBMIT: "retirement_accepting_workers_submit",
  RETIREMENT_TALENT_CONTRACT_CREATE_DOCUMENT: "retirement_TALENT_contract_create_document",
  RETIREMENT_TALENT_CONTRACT_CONVERT_REQUEST: "retirement_TALENT_contract_convert_request",
  RETIREMENT_TALENT_CONTRACT_SUBMIT: "retirement_TALENT_contract_submit",
  RENEW_VISA_GROUP_BASIC_INFORMATION: "renew_visa_group_basic_information",
  RENEW_VISA_GROUP_CREATE_DOCUMENT: "renew_visa_group_create_document",
  RENEW_VISA_GROUP_SUBMIT: "renew_visa_group_submit",
  RENEW_VISA_GROUP_AFTER_SUBMIT_APPLICATION: "renew_visa_group_after_submit_application",
  RENEW_VISA_GROUP_AFTER_SUBMIT_APPLICATION_RESULT: "renew_visa_group_after_submit_application_result",
  RENEW_VISA_GROUP_UPDATE_RESIDENCE_CARD: "renew_visa_group_update_residence_card",
  COE_BASIC_INFORMATION: "coe_basic_information",
  COE_CREATE_DOCUMENT: "coe_create_document",
  COE_SUBMIT: "coe_submit",
  COE_AFTER_SUBMIT_APPLICATION: "coe_after_submit_application",
  COE_AFTER_SUBMIT_APPLICATION_RESULT: "coe_after_submit_application_result",
  COE_UPDATE_RESIDENCE_CARD: "coe_update_residence_card",
  COE_GROUP_BASIC_INFORMATION: "coe_group_basic_information",
  COE_GROUP_CREATE_DOCUMENT: "coe_group_create_document",
  COE_GROUP_SUBMIT: "coe_group_submit",
  COE_GROUP_AFTER_SUBMIT_APPLICATION: "coe_group_after_submit_application",
  COE_GROUP_AFTER_SUBMIT_APPLICATION_RESULT: "coe_group_after_submit_application_result",
  COE_GROUP_UPDATE_RESIDENCE_CARD: "coe_group_update_residence_card",
};

export type TDocumentStepKey = valueOf<typeof DOCUMENT_STEP_KEYS>;

export const DOCUMENT_GROUPS = {
  [DOCUMENT_KEYS.ACCEPTANCE_ACTIVITY]: [
    DOCUMENT_STEP_KEYS.ACCEPTANCE_ACTIVITY_CREATE_DOCUMENT,
    DOCUMENT_STEP_KEYS.ACCEPTANCE_ACTIVITY_UPLOAD_DOCUMENT,
    DOCUMENT_STEP_KEYS.ACCEPTANCE_ACTIVITY_SUBMIT,
  ],
  [DOCUMENT_KEYS.LIFE_SUPPORT]: [
    DOCUMENT_STEP_KEYS.LIFE_SUPPORT_CREATE_DOCUMENT,
    DOCUMENT_STEP_KEYS.LIFE_SUPPORT_CONFIRM_DISCUSSION_RECORD,
    DOCUMENT_STEP_KEYS.LIFE_SUPPORT_SUBMIT,
  ],
  [DOCUMENT_KEYS.ANY_TIME]: [DOCUMENT_STEP_KEYS.ANY_TIME_CREATE_DOCUMENT, DOCUMENT_STEP_KEYS.ANY_TIME_SUBMIT],
  [DOCUMENT_KEYS.RETIREMENT_ACCEPTING_WORKERS]: [
    DOCUMENT_STEP_KEYS.RETIREMENT_ACCEPTING_WORKERS_CREATE_DOCUMENT,
    DOCUMENT_STEP_KEYS.RETIREMENT_ACCEPTING_WORKERS_CREATE_CONVERT_REQUEST,
    DOCUMENT_STEP_KEYS.RETIREMENT_ACCEPTING_WORKERS_SUBMIT,
  ],
  [DOCUMENT_KEYS.RETIREMENT_TALENT_CONTRACT]: [
    DOCUMENT_STEP_KEYS.RETIREMENT_TALENT_CONTRACT_CREATE_DOCUMENT,
    DOCUMENT_STEP_KEYS.RETIREMENT_TALENT_CONTRACT_CONVERT_REQUEST,
    DOCUMENT_STEP_KEYS.RETIREMENT_TALENT_CONTRACT_SUBMIT,
  ],
};

export const DOCUMENT_STATUS = {
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
  CONVERTING: "converting",
  CONVERT_COMPLETE: "convert_complete",
  ERROR: "error",
  SKIP: "skip",
};

export type TDocumentStatus = valueOf<typeof DOCUMENT_STATUS>;

export const DOCUMENT_STATUS_LABEL = {
  [DOCUMENT_STATUS.IN_PROGRESS]: "未完了",
  [DOCUMENT_STATUS.COMPLETE]: "編集完了",
  [DOCUMENT_STATUS.CONVERTING]: "変換中",
  [DOCUMENT_STATUS.CONVERT_COMPLETE]: "変換完了",
  [DOCUMENT_STATUS.ERROR]: "エラー",
  [DOCUMENT_STATUS.SKIP]: "スキップ",
};

export const CONVERT_STATUS = {
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
  ERROR: "error",
};

export type TConvertStatus = valueOf<typeof CONVERT_STATUS>;

export const CONVERT_STATUS_LABEL = {
  [CONVERT_STATUS.COMPLETE]: "編集完了",
  [CONVERT_STATUS.IN_PROGRESS]: "未完了",
  [CONVERT_STATUS.ERROR]: "エラー",
};

export const TALENT_RETIREMENT_DOCUMENT_STEP_STATUS = {
  INCOMPLETE: "incomplete",
  PROCESSING: "processing",
  CREATING: "creating",
  COMPLETE: "complete",
  ERROR: "error",
};

export type TTalentRetirementDocumentStepStatus = valueOf<typeof TALENT_RETIREMENT_DOCUMENT_STEP_STATUS>;

export const TALENT_RETIREMENT_DOCUMENT_STEP_STATUS_LABEL = {
  [TALENT_RETIREMENT_DOCUMENT_STEP_STATUS.INCOMPLETE]: "未完了",
  [TALENT_RETIREMENT_DOCUMENT_STEP_STATUS.PROCESSING]: "処理中",
  [TALENT_RETIREMENT_DOCUMENT_STEP_STATUS.CREATING]: "作成中",
  [TALENT_RETIREMENT_DOCUMENT_STEP_STATUS.COMPLETE]: "完了",
  [TALENT_RETIREMENT_DOCUMENT_STEP_STATUS.ERROR]: "エラー",
};

export const RENEW_VISA_DOCUMENT_STEP_STATUS = {
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
};

export const RENEW_VISA_DOCUMENT_STATUS = {
  NOT_CREATED: "not_created",
  IN_PROGRESS: "in_progress",
  UNDER_REVIEW: "under_review",
  APPROVED: "approved",
  COMPLETE: "complete",
  WITHDRAWN: "withdrawn",
  REJECTED: "rejected",
};

export type TRenewVisaDocumentStatus = valueOf<typeof RENEW_VISA_DOCUMENT_STATUS>;

export const RENEW_VISA_DOCUMENT_STATUS_LABEL = {
  [RENEW_VISA_DOCUMENT_STATUS.NOT_CREATED]: "未作成",
  [RENEW_VISA_DOCUMENT_STATUS.IN_PROGRESS]: "準備中",
  [RENEW_VISA_DOCUMENT_STATUS.UNDER_REVIEW]: "審査中",
  [RENEW_VISA_DOCUMENT_STATUS.APPROVED]: "許可済",
  [RENEW_VISA_DOCUMENT_STATUS.COMPLETE]: "完了",
  [RENEW_VISA_DOCUMENT_STATUS.WITHDRAWN]: "取り下げ",
  [RENEW_VISA_DOCUMENT_STATUS.REJECTED]: "不許可",
};

export const RENEW_VISA_APPLICATION_RESULT = {
  APPROVED: "approved",
  WITHDRAWN: "withdrawn",
  REJECTED: "rejected",
};

export const RENEW_VISA_APPLICATION_RESULT_LABEL = {
  [RENEW_VISA_APPLICATION_RESULT.APPROVED]: "申請許可",
  [RENEW_VISA_APPLICATION_RESULT.WITHDRAWN]: "取り下げ",
  [RENEW_VISA_APPLICATION_RESULT.REJECTED]: "不許可",
};

export type TRenewVisaApplicationResult = valueOf<typeof RENEW_VISA_APPLICATION_RESULT>;

export const COE_DOCUMENT_STEP_STATUS = {
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
};

export const COE_DOCUMENT_STATUS = {
  NOT_CREATED: "not_created",
  IN_PROGRESS: "in_progress",
  UNDER_REVIEW: "under_review",
  APPROVED: "approved",
  COMPLETE: "complete",
  WITHDRAWN: "withdrawn",
  REJECTED: "rejected",
};

export type TCoeDocumentStatus = valueOf<typeof COE_DOCUMENT_STATUS>;

export const COE_DOCUMENT_STATUS_LABEL = {
  [COE_DOCUMENT_STATUS.NOT_CREATED]: "未作成",
  [COE_DOCUMENT_STATUS.IN_PROGRESS]: "準備中",
  [COE_DOCUMENT_STATUS.UNDER_REVIEW]: "審査中",
  [COE_DOCUMENT_STATUS.APPROVED]: "許可済",
  [COE_DOCUMENT_STATUS.COMPLETE]: "完了",
  [COE_DOCUMENT_STATUS.WITHDRAWN]: "取り下げ",
  [COE_DOCUMENT_STATUS.REJECTED]: "不許可",
};

export const COE_APPLICATION_RESULT = {
  APPROVED: "approved",
  WITHDRAWN: "withdrawn",
  REJECTED: "rejected",
};

export const COE_APPLICATION_RESULT_LABEL = {
  [COE_APPLICATION_RESULT.APPROVED]: "申請許可",
  [COE_APPLICATION_RESULT.WITHDRAWN]: "取り下げ",
  [COE_APPLICATION_RESULT.REJECTED]: "不許可",
};

export type TCoeApplicationResult = valueOf<typeof COE_APPLICATION_RESULT>;

export const COE_APPLICATION_TYPE = {
  CERTIFICATION_APPLICATION: "certification_application",
  CHANGE_APPLICATION: "change_application",
};

export const COE_APPLICATION_TYPE_LABEL = {
  [COE_APPLICATION_TYPE.CERTIFICATION_APPLICATION]: "認定申請",
  [COE_APPLICATION_TYPE.CHANGE_APPLICATION]: "変更申請",
};

export type TCoeApplicationType = valueOf<typeof COE_APPLICATION_TYPE>;

export const COE_HUMAN_RESOURCES_ROUTE = {
  SUPPLIER: "supplier",
  OTHER: "other",
};

export type TCoeHumanResourcesRoute = valueOf<typeof COE_HUMAN_RESOURCES_ROUTE>;

export const CONVERT_DOCUMENT_STATUS = {
  IN_PROGRESS: "in_progress",
  COMPLETE: "complete",
  ERROR: "error",
  SKIP: "skip",
  CONVERTING: "converting",
  COMPLETED_CONVERT: "completed_convert",
};

export type TConvertDocumentStatus = valueOf<typeof CONVERT_DOCUMENT_STATUS>;

export const CONVERT_DOCUMENT_STATUS_LABEL = {
  [CONVERT_DOCUMENT_STATUS.COMPLETE]: "編集完了",
  [CONVERT_DOCUMENT_STATUS.IN_PROGRESS]: "未完了",
  [CONVERT_DOCUMENT_STATUS.ERROR]: "エラー",
  [CONVERT_DOCUMENT_STATUS.CONVERTING]: "変換中",
  [CONVERT_DOCUMENT_STATUS.COMPLETED_CONVERT]: "変換完了",
};

export const SUPPORT_IMPLEMENTATION_STATUS = {
  SUPPORTED: "supported",
  NOT_SUPPORTED: "not_supported",
  NO_PARTICIPANT: "no_participant",
};

export type TSupportImplementationStatus = valueOf<typeof SUPPORT_IMPLEMENTATION_STATUS>;

export const SUPPORT_IMPLEMENTATION_STATUS_OPTIONS = [
  { label: "実施", value: SUPPORT_IMPLEMENTATION_STATUS.SUPPORTED },
  { label: "未実施", value: SUPPORT_IMPLEMENTATION_STATUS.NOT_SUPPORTED },
  { label: "支援対象者なし", value: SUPPORT_IMPLEMENTATION_STATUS.NO_PARTICIPANT },
];

export const TALENT_TRAINING_STATUS = {
  UN_SELECTED: "un_selected",
  YES: "yes",
  NO: "no",
};

export type TTalentTrainingStatus = valueOf<typeof TALENT_TRAINING_STATUS>;

export const TALENT_CAREER_LOCATION = {
  JAPAN: "japan",
  OUTSIDE_JAPAN: "outside_japan",
};

export type TTalentCareerLocation = valueOf<typeof TALENT_CAREER_LOCATION>;

export const TALENT_RESIDENCE_STATUS = {
  SPECIFIC_SKILL_ONE: "specific_skill_one",
  SPECIFIC_SKILL_TWO: "specific_skill_two",
  SPECIFIC_ACTIVITY: "specific_activity",
};

export type TTalentResidenceStatus = valueOf<typeof TALENT_RESIDENCE_STATUS>;

export const TALENT_RESIDENCE_PERIOD = {
  ONE_MONTH: "one_month",
  TWO_MONTH: "two_month",
  THREE_MONTH: "three_month",
  FOUR_MONTH: "four_month",
  FIVE_MONTH: "five_month",
  SIX_MONTH: "six_month",
  SEVEN_MONTH: "seven_month",
  EIGHT_MONTH: "eight_month",
  NINE_MONTH: "nine_month",
  TEN_MONTH: "ten_month",
  ELEVEN_MONTH: "eleven_month",
  TWELVE_MONTH: "twelve_month",
  ONE_YEAR: "one_year",
  THREE_YEAR: "three_year",
  FIVE_YEAR: "five_year",
};

export type TTalentResidencePeriod = valueOf<typeof TALENT_RESIDENCE_PERIOD>;

export const MAINTENANCE_MODE = {
  SYSTEM_NORMAL_OPERATION: "system_normal_operation",
  MAINTENANCE_READABLE: "maintenance_readable",
  MAINTENANCE_UNUSABLE: "maintenance_unusable",
};

export const SUPPORT_SPECIFIED_SKILL = {
  NAME_OR_TITLE: "name_or_title",
  ADDRESS: "address",
  CORPORATE_NUMBER: "corporate_number",
  OTHER: "other",
  SUPPORT_MANAGER_NAME_AND_POSITION: "support_manager_name_and_position",
  NUMBER_OF_FOREIGN_WORKERS: "number_of_foreign_workers",
  NUMBER_OF_SUPPORT_STAFF: "number_of_support_staff",
  OFFICE_LOCATION: "office_location",
};

export const YES_NO_ANSWER = {
  YES: "yes",
  NO: "no",
};

export type TYesNoAnswer = valueOf<typeof YES_NO_ANSWER>;

export const YES_NO_AND_NOT_APPLICABLE_ANSWER = {
  YES: "yes",
  NO: "no",
  NOT_APPLICABLE: "not_applicable",
};

export type TYesNoAndNotApplicableAnswer = valueOf<typeof YES_NO_AND_NOT_APPLICABLE_ANSWER>;

export const COMPANY_SUPPORTER_ROLE = {
  CONTACT: "contact",
  SUPPORT_MANAGER: "support_manager",
  SUPPORT: "support",
};

export const JOB_RESIDENCE_STATUS = {
  SKILL_TRAINING: 1,
  SPECIFIED_SKILL: 2,
  ENGINEER_HUMANITIES: 3,
  OTHER: 4,
} as const;

export const RSO_SUPPORTER_ROLE = {
  MANAGER: "manager",
  STAFF: "staff",
  CONTACT_PERSON: "contact_person",
};

export type TRsoSupporterRole = valueOf<typeof RSO_SUPPORTER_ROLE>;

export const RSO_SUPPORTER_ROLE_LABEL = {
  [RSO_SUPPORTER_ROLE.MANAGER]: "支援責任者",
  [RSO_SUPPORTER_ROLE.STAFF]: "支援担当者",
  [RSO_SUPPORTER_ROLE.CONTACT_PERSON]: "連絡担当者",
};

export const PAYMENT_DATE = {
  CURRENT_MONTH: "current_month",
  NEXT_MONTH: "next_month",
};

export const JAPAN_NATIONALITY_ID = 47;
export const VIETNAM_NATIONALITY_ID = 40;
export const THAILAND_NATIONALITY_ID = 24;
export const CAMBODIA_NATIONALITY_ID = 16;
export const MYANMAR_NATIONALITY_ID = 42;
export const INDONESIA_NATIONALITY_ID = 10;
// Philippines (フィリピン): 37
// Sri Lanka (スリランカ): 23
// Bangladesh (バングラディシュ): 35
// India (インド): 9
// Singapore (シンガポール): 22
// Malaysia (マレーシア): 41
export const EN_NATIONALITY_ID = [37, 23, 35, 9, 22, 41];

export const RESIDENCE_STATUS_FOR_COE_NAME = "未取得（認定申請前）";

export const CHECK_UPDATE_TALENT_RESULT = {
  CAN_UPDATE: "can_update",
  CAN_UPDATE_AND_DELETE_RENEW_VISA: "can_update_and_delete_renew_visa",
  CAN_UPDATE_AND_UPDATE_RENEW_VISA: "can_update_and_update_renew_visa",
  CAN_UPDATE_AND_CREATE_RENEW_VISA: "can_update_and_create_renew_visa",
  CAN_NOT_UPDATE: "can_not_update",
  CAN_UPDATE_AND_CREATE_COE: "can_update_and_create_coe",
  CAN_UPDATE_AND_DELETE_COE: "can_update_and_delete_coe",
  CAN_UPDATE_AND_CREATE_CONDITION_DOCUMENT: "can_update_and_create_condition_document",
  CAN_UPDATE_AND_DELETE_CONDITION_DOCUMENT: "can_update_and_delete_condition_document",
  DELETE_COE_AND_SHOW_CONFIRM_MODAL: "delete_coe_and_show_confirm_modal",
};

export type TCheckUpdateTalentResult = valueOf<typeof CHECK_UPDATE_TALENT_RESULT>;

export type TSettingColumn = {
  key: string;
  name: string;
  isHidden: boolean;
};

export const SUPPLIER_TYPE = {
  DISPATCH: "dispatch",
  RECRUITMENT: "recruitment",
};

export const UPLOAD_FILE_ITEM = {
  DRAFT: "draft",
  OFFICIAL: "official",
};

export type TUploadFileItem = valueOf<typeof UPLOAD_FILE_ITEM>;

export const UPLOAD_FILE_ITEM_LABEL = [
  { value: UPLOAD_FILE_ITEM.DRAFT, label: "下書き" },
  { value: UPLOAD_FILE_ITEM.OFFICIAL, label: "提出版" },
];

export const CHECK_DELETE_TALENT_RESULT = {
  CAN_DELETE: "can_delete",
  CAN_DELETE_AND_DELETE_RENEW_VISA: "can_delete_and_delete_renew_visa",
  DELETE_COE_AND_SHOW_CONFIRM_MODAL: "delete_coe_and_show_confirm_modal",
  DELETE_RENEW_VISA_AND_SHOW_CONFIRM_MODAL: "delete_renew_visa_and_show_confirm_modal",
  DELETE_CONDITION_DOCUMENT_AND_SHOW_CONFIRM_MODAL: "delete_condition_document_and_show_confirm_modal",
  CAN_DELETE_AND_DELETE_COE: "can_delete_and_delete_coe",
  CAN_DELETE_AND_DELETE_CONDITION_DOCUMENT: "can_delete_and_delete_condition_document",
  CAN_NOT_DELETE: "can_not_delete",
};

export type TCheckDeleteTalentResult = valueOf<typeof CHECK_DELETE_TALENT_RESULT>;

export const CONTRACT_RENEWAL_STATUS = {
  AUTOMATICALLY: "automatically",
  MAY_BE: "may_be",
  IS_NOT: "is_not",
};

export const CONTRACT_CONDITION_RENEWAL = {
  WORK_VOLUME: "work_volume",
  WORK_RECORD_ATTITUDE: "work_record_attitude",
  TASK_EXECUTION_CAPABILITY: "task_execution_capability",
  COMPANY_PERFORMANCE: "company_performance",
  WORK_PROGRESS: "work_progress",
};

export const TALENT_TYPE = {
  DIRECT: "direct",
  DISPATCH: "dispatch",
};

export const WORKING_MODE = {
  FLEXIBLE_WORK: "flexible_work",
  SHIFT_WORK: "shift_work",
};

export type TWorkingMode = valueOf<typeof WORKING_MODE>;

export const PAYMENT_METHOD = {
  BANKING: "banking",
  CASH: "cash",
};

export const INSURANCE_TYPE = {
  WELFARE_PENSION: "welfare_pension", // 厚生年金
  HEALTH_INSURANCE: "health_insurance", // 健康保険
  TALENT_INSURANCE: "talent_insurance", // 雇用保険
  WORKERS_COMPENSATION: "workers_compensation", // 労災保険
  NATIONAL_PENSION: "national_pension", // 国民年金
  NATIONAL_HEALTH_INSURANCE: "national_health_insurance", // 国民健康保険
  OTHER: "other", // その他
};

export type TInsuranceType = valueOf<typeof INSURANCE_TYPE>;

export const LANGUAGE_CODE = {
  VI: "vi",
  MY: "my",
  ID: "id",
  EN: "en",
};

export type TLanguageCode = valueOf<typeof LANGUAGE_CODE>;

export const NON_REGULAR_DAY = {
  WEEK: "week",
  MONTH: "month",
};

export const WAGE_TYPE = {
  MONTHLY: "monthly",
  DAILY: "daily",
  HOURLY: "hourly",
};

export const CHECK_CREATE_COMPANY_RESULT = {
  CAN_CREATE_AND_SHOW_DUPLICATE_MANAGEMENT_NUMBER: "can_create_and_show_duplicate_management_number",
};

export type TCheckCreateCompanyResult = valueOf<typeof CHECK_CREATE_COMPANY_RESULT>;

export const CHECK_UPDATE_COMPANY_RESULT = {
  CAN_UPDATE_AND_SHOW_DUPLICATE_MANAGEMENT_NUMBER: "can_update_and_show_duplicate_management_number",
};

export type TCheckUpdateCompanyResult = valueOf<typeof CHECK_UPDATE_COMPANY_RESULT>;

export const COMPANY_CONDITION = {
  A: "a",
  B: "b",
  C: "c",
  D: "d",
  E: "e",
  NONE_A_TO_E: "none_a_to_e",
};

export type TCompanyCondition = valueOf<typeof COMPANY_CONDITION>;

// Talent
export enum ETalentStatus {
  ACTIVE = "active",
}

export const TALENT_LOCATION = {
  LOCAL: 1,
  OVERSEA: 2,
};

export const TALENT_LOCATION_OPTIONS = [
  { label: "日本", value: TALENT_LOCATION.LOCAL },
  { label: "日本以外", value: TALENT_LOCATION.OVERSEA },
];

export enum ERecruitmentStatus {
  DRAFT = 1,
  JOB_SEEKING = 2,
  OFFER_ACCEPTED = 3,
  ALREADY_HIRED = 4,
  OTHER_COMPANY_OFFER = 5,
  JOB_HUNTING_FINISHED = 6,
}

// Master attachment document type IDs.
// Synced with BE seeder `src/seeds/initMasterAttachmentDocumentTypes.seeder.ts`.
// IDs are stable (insert order) — DO NOT renumber.
export enum EAttachmentDocumentType {
  RESIDENCE_CARD_FRONT = 1, // 在留カード（表）
  RESIDENCE_CARD_BACK = 2, // 在留カード（裏）
  RESUME = 3, // 履歴書
  GRADUATION_CERTIFICATE = 4, // 卒業証書
  JAPANESE_SKILL_CERTIFICATE = 5, // 特定技能合格証等
  TRAINING_COMPLETION = 6, // 技能実習修了書等
  JAPANESE_LANGUAGE_CERTIFICATE = 7, // 日本語合格証
  PHOTO = 8, // 写真
}

// Residence card front + back — required (as a group) for case-match validation
// when the talent's location = LOCAL.
export const RESIDENCE_CARD_DOCUMENT_TYPE_IDS: readonly number[] = [
  EAttachmentDocumentType.RESIDENCE_CARD_FRONT,
  EAttachmentDocumentType.RESIDENCE_CARD_BACK,
];

// Document types managed by the case-match certificate field ("証明書など").
// A talent file is surfaced in the case-match edit/detail view if any of its
// `attachmentDocumentTypeIds` intersects this set.
export const CASE_MATCH_DOCUMENT_TYPE_IDS: readonly number[] = [
  EAttachmentDocumentType.RESIDENCE_CARD_FRONT,
  EAttachmentDocumentType.RESIDENCE_CARD_BACK,
  EAttachmentDocumentType.JAPANESE_SKILL_CERTIFICATE,
  EAttachmentDocumentType.JAPANESE_LANGUAGE_CERTIFICATE,
];

export const ATTACHED_DOCUMENT_COLOR_NAME = {
  SUCCESS: "success",
  INCOMPLETE: "incomplete",
  PROGRESS: "progress",
  ERROR: "error",
  UNCREATED: "uncreated",
  DISABLE: "disable",
  NORMAL: "normal",
  PURPLE: "purple",
  DEEP_PURPLE: "deep_purple",
  LIGHT_BLUE: "light_blue",
  TEAL: "teal",
  LIGHT_GREEN: "light_green",
  YELLOW: "yellow",
};

export const ATTACHED_DOCUMENT_COLOR = {
  [ATTACHED_DOCUMENT_COLOR_NAME.SUCCESS]: {
    bgColor: "#C8E6C9",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.INCOMPLETE]: {
    bgColor: "#FFE0B2",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.PROGRESS]: {
    bgColor: "#BBDEFB",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.ERROR]: {
    bgColor: "#FFCDD2",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.DISABLE]: {
    bgColor: "#E6E7E8",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.UNCREATED]: {
    bgColor: "#E6E7E8",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.NORMAL]: {
    bgColor: "#FFFFFF",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.PURPLE]: {
    bgColor: "#E1BEE7",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.DEEP_PURPLE]: {
    bgColor: "#D1C4E9",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.LIGHT_BLUE]: {
    bgColor: "#B3E5FC",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.TEAL]: {
    bgColor: "#B2DFDB",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.LIGHT_GREEN]: {
    bgColor: "#DCEDC8",
    color: "#2E3436",
  },
  [ATTACHED_DOCUMENT_COLOR_NAME.YELLOW]: {
    bgColor: "#FFF9C4",
    color: "#2E3436",
  },
};

export const JOB_STATUS = {
  DRAFT: "draft",
  SCHEDULED: "scheduled",
  OPEN: "open",
  PAUSED: "paused",
  CLOSED: "closed",
};

export type TJobStatus = valueOf<typeof JOB_STATUS>;

export const YES_NO_PENDING_STATUS = {
  YES: "yes",
  NO: "no",
  PENDING: "pending",
};

export type TYesNoPendingStatus = valueOf<typeof YES_NO_PENDING_STATUS>;

export enum EMatchContractStatus {
  DURING_CONTRACT = "during_contract",
  TRIAL = "trial",
  CANCELED_CONTRACT = "canceled_contract",
  TRIAL_CANCELLED = "trial_cancelled",
}

export const LABEL_DATE_CONTRACT_STATUS_MATCH = {
  [EMatchContractStatus.DURING_CONTRACT]: "契約日",
  [EMatchContractStatus.TRIAL]: "トライアル開始日",
  [EMatchContractStatus.CANCELED_CONTRACT]: "解約日",
  [EMatchContractStatus.TRIAL_CANCELLED]: "解約日",
};

export const EXPIRY_STATUS = {
  EXPIRED: "expired",
  EXPIRING_SOON: "expiring_soon",
} as const;

export enum ECaseMatchStatus {
  BEFORE_PROPOSAL = 0,
  PROPOSING = 1,
  UNDER_SELECTION = 2,
  OFFER = 3,
  OFFER_ACCEPTED = 4,
  ALREADY_HIRED = 5,
  FAILED = 6,
  CANDIDATE_WITHDRAWN = 7,
  OFFER_CANCELLED = 8,
  SELECTION_CANCELLED = 9,
}

export const CaseMatchStatusOptions = [
  {
    value: ECaseMatchStatus.BEFORE_PROPOSAL,
    label: "推薦前",
    color: String(ECaseMatchStatus.BEFORE_PROPOSAL),
  },
  {
    value: ECaseMatchStatus.PROPOSING,
    label: "推薦中",
    color: String(ECaseMatchStatus.PROPOSING),
  },
  {
    value: ECaseMatchStatus.UNDER_SELECTION,
    label: "選考中",
    color: String(ECaseMatchStatus.UNDER_SELECTION),
  },
  {
    value: ECaseMatchStatus.OFFER,
    label: "内定",
    color: String(ECaseMatchStatus.OFFER),
  },
  {
    value: ECaseMatchStatus.OFFER_ACCEPTED,
    label: "内定受諾",
    color: String(ECaseMatchStatus.OFFER_ACCEPTED),
  },
  {
    value: ECaseMatchStatus.ALREADY_HIRED,
    label: "入社済み",
    color: String(ECaseMatchStatus.ALREADY_HIRED),
  },
  {
    value: ECaseMatchStatus.FAILED,
    label: "選考不合格",
    color: String(ECaseMatchStatus.FAILED),
  },
  {
    value: ECaseMatchStatus.CANDIDATE_WITHDRAWN,
    label: "本人辞退",
    color: String(ECaseMatchStatus.CANDIDATE_WITHDRAWN),
  },
  {
    value: ECaseMatchStatus.OFFER_CANCELLED,
    label: "内定キャンセル",
    color: String(ECaseMatchStatus.OFFER_CANCELLED),
  },
  {
    value: ECaseMatchStatus.SELECTION_CANCELLED,
    label: "選考キャンセル",
    color: String(ECaseMatchStatus.SELECTION_CANCELLED),
  },
];

export enum EForeignLanguage {
  ENGLISH = 1,
  CHINESE = 2,
  OTHER = 3,
}

export const CASE_MATCH_STATUS_COLOR = {
  [String(ECaseMatchStatus.BEFORE_PROPOSAL)]: { bgColor: "#E6E7E8", color: "#2E3436" },
  [String(ECaseMatchStatus.PROPOSING)]: { bgColor: "#BBDEFB", color: "#2E3436" },
  [String(ECaseMatchStatus.UNDER_SELECTION)]: { bgColor: "#BBDEFB", color: "#2E3436" },
  [String(ECaseMatchStatus.OFFER)]: { bgColor: "#FFF9C4", color: "#2E3436" },
  [String(ECaseMatchStatus.OFFER_ACCEPTED)]: { bgColor: "#C8E6C9", color: "#2E3436" },
  [String(ECaseMatchStatus.ALREADY_HIRED)]: { bgColor: "#C8E6C9", color: "#2E3436" },
  [String(ECaseMatchStatus.FAILED)]: { bgColor: "#FFCDD2", color: "#2E3436" },
  [String(ECaseMatchStatus.CANDIDATE_WITHDRAWN)]: { bgColor: "#FFCDD2", color: "#2E3436" },
  [String(ECaseMatchStatus.OFFER_CANCELLED)]: { bgColor: "#FFCDD2", color: "#2E3436" },
  [String(ECaseMatchStatus.SELECTION_CANCELLED)]: { bgColor: "#FFCDD2", color: "#2E3436" },
};

/**
 * Terminal statuses represent end states (failed/cancelled/withdrawn).
 * These are not part of the normal progression flow (0→1→2→3→4→5).
 */
export const CASE_MATCH_TERMINAL_STATUSES = [
  ECaseMatchStatus.FAILED,
  ECaseMatchStatus.CANDIDATE_WITHDRAWN,
  ECaseMatchStatus.OFFER_CANCELLED,
  ECaseMatchStatus.SELECTION_CANCELLED,
] as const;

// Runtime environment injected at build time via REACT_APP_APP_ENV.
export const APP_ENV = {
  DEV: "DEV",
  STG: "STG",
  PROD: "PROD",
} as const;

export type TAppEnv = valueOf<typeof APP_ENV>;
